import React, { useState } from 'react';
import { Mail, ArrowRight, Sparkles } from 'lucide-react';
import { EARTH, TYPOGRAPHY } from '../theme/constants';
import { CONFIG } from '../config';
import { track } from '../lib/analytics';

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: '10px',
  border: `1.5px solid ${EARTH.stone[200]}`,
  backgroundColor: '#fff',
  fontSize: '15px',
  fontFamily: TYPOGRAPHY.body,
  color: EARTH.ink,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

export function TrialEmailGate({ onSubmit, onCancel }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setLoading(true);

    // Subscribe via MailerLite (same-origin function keeps the API key server-side).
    // Non-blocking: a failure here must never stop the user from entering their trial.
    try {
      await fetch('/api/subscribe-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || undefined }),
      });
    } catch (_) {
      // ESP failure is non-fatal — still let the user in
    }

    track('trial_started');
    setLoading(false);
    onSubmit(email, name);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(26,26,26,0.65)', backdropFilter: 'blur(8px)',
      fontFamily: TYPOGRAPHY.body,
    }}>
      <div style={{
        backgroundColor: EARTH.cream,
        borderRadius: '20px',
        padding: '48px 44px',
        maxWidth: '460px',
        width: '92%',
        boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
        border: `1px solid ${EARTH.stone[200]}`,
        textAlign: 'center',
      }}>
        {/* Icon */}
        <div style={{
          width: '60px', height: '60px', borderRadius: '50%',
          backgroundColor: EARTH.terracotta[100],
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <Sparkles style={{ width: '28px', height: '28px', color: EARTH.terracotta[500] }} />
        </div>

        <h2 style={{
          fontFamily: TYPOGRAPHY.display,
          fontSize: '26px',
          color: EARTH.ink,
          marginBottom: '8px',
          textTransform: 'lowercase',
          letterSpacing: '0.5px',
        }}>
          start your free trial
        </h2>

        <p style={{
          color: EARTH.stone[500],
          fontSize: '14px',
          lineHeight: '1.6',
          marginBottom: '32px',
        }}>
          {CONFIG.TRIAL_DAYS} days free. No credit card. No commitment.
          <br />
          We'll send you helpful tips along the way.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Name (optional) */}
          <div style={{ marginBottom: '12px', textAlign: 'left' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: EARTH.stone[500], marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Name <span style={{ fontWeight: 400, textTransform: 'none', opacity: 0.7 }}>(optional)</span>
            </label>
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = EARTH.terracotta[400]}
              onBlur={(e) => e.target.style.borderColor = EARTH.stone[200]}
            />
          </div>

          {/* Email (required) */}
          <div style={{ marginBottom: '20px', textAlign: 'left' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: EARTH.stone[500], marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Email <span style={{ color: EARTH.terracotta[500] }}>*</span>
            </label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              required
              autoFocus
              style={{ ...inputStyle, borderColor: error ? EARTH.terracotta[400] : EARTH.stone[200] }}
              onFocus={(e) => e.target.style.borderColor = EARTH.terracotta[400]}
              onBlur={(e) => e.target.style.borderColor = error ? EARTH.terracotta[400] : EARTH.stone[200]}
            />
            {error && (
              <p style={{ color: EARTH.terracotta[600], fontSize: '12px', marginTop: '5px', textAlign: 'left' }}>
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !email}
            style={{
              width: '100%',
              padding: '13px 24px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 700,
              backgroundColor: loading || !email ? EARTH.stone[300] : EARTH.terracotta[500],
              color: '#fff',
              border: 'none',
              cursor: loading || !email ? 'not-allowed' : 'pointer',
              fontFamily: TYPOGRAPHY.body,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background-color 0.2s',
              marginBottom: '12px',
            }}
            onMouseEnter={(e) => { if (!loading && email) e.currentTarget.style.backgroundColor = EARTH.terracotta[600]; }}
            onMouseLeave={(e) => { if (!loading && email) e.currentTarget.style.backgroundColor = EARTH.terracotta[500]; }}
          >
            {loading ? 'Starting…' : (
              <>
                Start Free Trial
                <ArrowRight style={{ width: '18px', height: '18px' }} />
              </>
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            style={{
              background: 'none', border: 'none',
              color: EARTH.stone[400], fontSize: '13px',
              cursor: 'pointer', fontFamily: TYPOGRAPHY.body,
            }}
          >
            Cancel
          </button>
        </form>

        <p style={{ marginTop: '20px', fontSize: '11px', color: EARTH.stone[400], lineHeight: '1.5' }}>
          No spam. Unsubscribe anytime. Your data is never sold.
        </p>
      </div>
    </div>
  );
}
